import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useApproveBlueprint,
  useBlueprint,
  useInferBlueprint,
  useKnowledgePoints,
  type BlueprintCell,
} from "@/api/school";

// 题型枚举 — 与后端 blueprint.py ITEM_TYPES_P1 保持一致,老师可在矩阵里改。
const ITEM_TYPES = ["mcq", "cloze", "short_answer", "translation", "essay"] as const;

/** 命题蓝图 — Exam Engine P1 FR-A. Blueprint-first: the teacher reviews
 *  scope, generates the matrix, hand-tunes it, and approves BEFORE any
 *  item generation. Scope = taught units only; adding an untaught KP
 *  requires an explicit warning-confirm (边界16). */
export default function BlueprintPage() {
  const { t } = useTranslation();
  const { examId } = useParams<{ examId: string }>();
  const bundle = useBlueprint(examId);
  const infer = useInferBlueprint(examId!);
  const approve = useApproveBlueprint(examId!);
  const allKps = useKnowledgePoints("approved", bundle.data?.exam?.subject_id);

  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState("");
  // 2026-09-15:没有已讲单元/知识点时按主题圈范围(每行一个),解除对教学进度的硬绑定。
  const [topicsText, setTopicsText] = useState("");
  const topics = topicsText.split(/\n|[,,;;]/).map((x) => x.trim()).filter(Boolean);
  const [matrix, setMatrix] = useState<BlueprintCell[] | null>(null);

  const bp = bundle.data?.blueprint ?? null;
  const exam = bundle.data?.exam;
  const scope = bundle.data?.taught_scope ?? [];
  const effMatrix = matrix ?? bp?.matrix ?? [];
  const taughtIds = useMemo(() => new Set(scope.map((s) => s.kp_id)), [scope]);
  // 按主题临时建的知识点是 candidate,不在 approved 列表里 → 矩阵曾显示成
  // 代码(CUS-T-…)。后端随蓝图返回 kp_names(code→名字)兜底。
  const kpNames: Record<string, string> = { ...(bundle.data?.kp_names ?? {}), ...(infer.data?.kp_names ?? {}) };
  const addableKps = (allKps.data ?? []).filter((k) => k.parent_id !== null && !taughtIds.has(k.id));

  const total = effMatrix.reduce((acc, c) => acc + c.count * c.score_each, 0);
  const totalOk = exam ? Math.abs(total - exam.max_total) < 0.01 : false;

  const axisTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of effMatrix) m.set(c.section_axis, (m.get(c.section_axis) ?? 0) + c.count * c.score_each);
    return [...m.entries()];
  }, [effMatrix]);

  function updateCell(i: number, patch: Partial<BlueprintCell>) {
    const next = [...effMatrix];
    const cur = next[i];
    if (!cur) return;
    next[i] = { ...cur, ...patch };
    setMatrix(next);
  }

  if (bundle.isLoading) return <p className="p-6 text-sm text-slate-400">…</p>;
  if (!exam) return <p className="p-6 text-sm text-rose-500">exam not found</p>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5">
        <Link to="/exams" className="text-xs text-sky-600 hover:underline">← {t("blueprint.back")}</Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-1">
          {t("blueprint.title")} · {exam.title}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          {exam.class_name} · {t(`exams.types.${exam.type}`)} · {exam.exam_date} · {t("blueprint.fullMarks", { total: exam.max_total })}
        </p>
      </header>

      {/* Step 1 — scope */}
      <section className="mb-5 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">
          {t("blueprint.scopeHeader", { count: scope.length })}
        </h2>
        {scope.length === 0 && (
          <p className="text-sm text-amber-600">{t("blueprint.noTaught")}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {scope.map((s) => {
            const off = removed.has(s.kp_id);
            return (
              <button
                key={s.kp_id}
                type="button"
                title={`${s.units.join(" / ")} · ${s.last_taught}`}
                onClick={() => {
                  const next = new Set(removed);
                  if (off) next.delete(s.kp_id);
                  else next.add(s.kp_id);
                  setRemoved(next);
                }}
                className={`rounded border px-2 py-0.5 text-xs ${
                  off
                    ? "border-slate-200 text-slate-300 line-through"
                    : "border-emerald-300 bg-emerald-50 text-emerald-700"
                }`}
              >
                {s.name_zh}
              </button>
            );
          })}
          {[...added].map((id) => {
            const k = (allKps.data ?? []).find((x) => x.id === id);
            if (!k) return null;
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  const next = new Set(added);
                  next.delete(id);
                  setAdded(next);
                }}
                className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700"
              >
                {k.name_zh} ⚠︎
              </button>
            );
          })}
          <AddUntaught kps={addableKps.filter((k) => !added.has(k.id))} onAdd={(id) => setAdded(new Set([...added, id]))} />
        </div>
        <label className="mt-3 block">
          <span className="block text-xs font-medium text-slate-600 mb-1">{t("blueprint.topicsLabel")}</span>
          <textarea
            rows={scope.length === 0 ? 3 : 2}
            value={topicsText}
            onChange={(e) => setTopicsText(e.target.value)}
            placeholder={t("blueprint.topicsPlaceholder")}
            data-testid="blueprint-topics"
            className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
          />
          <span className="block text-xs text-slate-400 mt-1">{t("blueprint.topicsHint")}</span>
        </label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("blueprint.notesPlaceholder")}
          className="mt-3 w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
        />
        <button
          type="button"
          disabled={infer.isPending || (scope.length === 0 && added.size === 0 && topics.length === 0)}
          onClick={() => {
            setMatrix(null);
            infer.mutate({ add_kp_ids: [...added], remove_kp_ids: [...removed], notes, topics });
          }}
          className="mt-3 px-4 py-2 text-sm font-medium rounded bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50"
        >
          {infer.isPending ? t("blueprint.generating") : bp ? t("blueprint.regenerate") : t("blueprint.generate")}
        </button>
        {infer.isError && <p className="mt-2 text-sm text-rose-500">{String(infer.error)}</p>}
      </section>

      {/* Step 2 — matrix review */}
      {bp && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-700">
              {t("blueprint.matrixHeader", { version: bp.version })}
              {bp.approved_at && (
                <span className="ml-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] px-1.5 py-0.5">
                  {t("blueprint.approvedAt", { at: bp.approved_at.slice(0, 16).replace("T", " ") })}
                </span>
              )}
            </h2>
            <span className={`text-sm font-mono ${totalOk ? "text-emerald-600" : "text-rose-500"}`}>
              {total} / {exam.max_total}
            </span>
          </div>

          <div className="mb-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
            {axisTotals.map(([axis, sub]) => (
              <span key={axis} className="rounded bg-slate-50 border border-slate-200 px-1.5 py-0.5">
                {t(`exams.sections.${axis}`, axis)}: {sub}
              </span>
            ))}
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-1.5 pr-2">{t("blueprint.cols.kp")}</th>
                <th className="py-1.5 pr-2">{t("blueprint.cols.type")}</th>
                <th className="py-1.5 pr-2 w-20">{t("blueprint.cols.count")}</th>
                <th className="py-1.5 pr-2 w-24">{t("blueprint.cols.scoreEach")}</th>
                <th className="py-1.5 pr-2 w-20">{t("blueprint.cols.subtotal")}</th>
                <th className="py-1.5 w-8" />
              </tr>
            </thead>
            <tbody>
              {effMatrix.map((c, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-1.5 pr-2">
                    <span className="text-slate-800">{kpNames[c.kp_code] ?? scope.find((s) => s.code === c.kp_code)?.name_zh ?? (allKps.data ?? []).find((k) => k.code === c.kp_code)?.name_zh ?? c.kp_code}</span>
                    <span className="ml-1 text-[10px] text-slate-400">{t(`exams.sections.${c.section_axis}`, c.section_axis)}</span>
                  </td>
                  <td className="py-1.5 pr-2">
                    <select
                      value={c.item_type}
                      onChange={(e) => updateCell(i, { item_type: e.target.value })}
                      className="rounded border border-slate-200 px-1.5 py-0.5 text-slate-600"
                    >
                      {ITEM_TYPES.map((it) => (
                        <option key={it} value={it}>{t(`blueprint.itemTypes.${it}`, it)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="number" min={1} max={20} value={c.count}
                      onChange={(e) => updateCell(i, { count: Number(e.target.value) || 1 })}
                      className="w-16 rounded border border-slate-200 px-1.5 py-0.5"
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="number" min={0.5} step={0.5} value={c.score_each}
                      onChange={(e) => updateCell(i, { score_each: Number(e.target.value) || 0 })}
                      className="w-20 rounded border border-slate-200 px-1.5 py-0.5"
                    />
                  </td>
                  <td className="py-1.5 pr-2 font-mono text-slate-500">{c.count * c.score_each}</td>
                  <td className="py-1.5">
                    <button
                      type="button"
                      onClick={() => setMatrix(effMatrix.filter((_, j) => j !== i))}
                      className="text-rose-300 hover:text-rose-500"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 rounded bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600 whitespace-pre-wrap">
            {bp.scope_explanation}
          </div>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              disabled={!totalOk || approve.isPending || !!bp.approved_at}
              onClick={() => approve.mutate({ matrix: matrix ?? undefined })}
              className="px-4 py-2 text-sm font-medium rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {bp.approved_at ? t("blueprint.alreadyApproved") : t("blueprint.approve")}
            </button>
            {/* 组卷:直达试卷页并自动按蓝图生成,省去中间"按蓝图生成试卷"一步(Jarod 反馈)。 */}
            {bp.approved_at && (
              <Link
                to={`/exams/${examId}/paper?generate=1`}
                className="ml-2 px-4 py-2 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
              >
                {t("blueprint.toPaper")}
              </Link>
            )}
          </div>
          {!totalOk && (
            <p className="mt-1 text-right text-xs text-rose-500">{t("blueprint.totalMismatch", { total, max: exam.max_total })}</p>
          )}
          {approve.isError && <p className="mt-1 text-right text-xs text-rose-500">{String(approve.error)}</p>}
        </section>
      )}
    </div>
  );
}

function AddUntaught({ kps, onAdd }: { kps: { id: string; name_zh: string }[]; onAdd: (id: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (kps.length === 0) return null;
  return (
    <span className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-50"
      >
        {t("blueprint.addKp")}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 max-h-52 w-64 overflow-y-auto rounded border border-slate-200 bg-white p-2 shadow-lg">
          <p className="mb-1 text-[10px] text-amber-600">{t("blueprint.addKpWarn")}</p>
          {kps.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => {
                if (window.confirm(t("blueprint.addKpConfirm", { name: k.name_zh }))) {
                  onAdd(k.id);
                  setOpen(false);
                }
              }}
              className="block w-full rounded px-2 py-1 text-left text-xs text-slate-600 hover:bg-slate-50"
            >
              {k.name_zh}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
